import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Radio } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import {
  fetchCaptureSession,
  useCaptureControls,
  useCaptureSessions,
  useCaptureStatus,
} from '../captureApi';

/**
 * PTV2-1524: per-provider "Token capture (debug)" section at the bottom of the auth-provider detail
 * page. Collapsed by default (so it neither fetches nor renders until opened). When open, a platform
 * admin can Start / Stop / Clear a time-boxed capture of real logins on this provider's host and read
 * each captured login's decoded token claims + userinfo as pretty JSON — the ground-truth way to learn
 * an external IdP's claim shape when onboarding a client without a test account.
 */
export function LoginCaptureSection({ host }: { readonly host: string }): ReactElement {
  const { t } = useTranslation();
  const cp = 'routes.admin.authProviders.capture';

  const [open, setOpen] = useState(false);
  const [ttlMinutes, setTtlMinutes] = useState(15);
  const [viewing, setViewing] = useState<Record<string, unknown> | null>(null);
  const [viewError, setViewError] = useState(false);

  const statusQuery = useCaptureStatus(host, open);
  const sessionsQuery = useCaptureSessions(host, open);
  const { start, stop, clear } = useCaptureControls(host);

  const status = statusQuery.data;
  const busy = start.isPending || stop.isPending || clear.isPending;

  async function view(id: string): Promise<void> {
    setViewError(false);
    try {
      setViewing(await fetchCaptureSession(host, id));
    } catch {
      setViewError(true);
      setViewing(null);
    }
  }

  return (
    <section aria-labelledby="section-capture" className="space-y-4 rounded-md border border-border bg-card p-5">
      <button
        type="button"
        id="section-capture"
        className="flex w-full items-center gap-2 text-sm font-semibold text-foreground"
        onClick={() => {
          setOpen((v) => !v);
        }}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
        <Radio className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        {t(`${cp}.title`)}
        {status?.enabled === true && (
          <Badge variant="default" className="ml-1">
            {t(`${cp}.recording`)}
          </Badge>
        )}
      </button>

      {open && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">{t(`${cp}.help`)}</p>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              {t(`${cp}.window`)}
              <Input
                type="number"
                min={1}
                max={60}
                value={ttlMinutes}
                onChange={(e) => {
                  setTtlMinutes(Number(e.target.value));
                }}
                className="w-20"
              />
              {t(`${cp}.minutes`)}
            </label>
            <Button
              type="button"
              size="sm"
              loading={start.isPending}
              disabled={busy}
              onClick={() => {
                start.mutate(Math.max(1, Math.min(ttlMinutes, 60)) * 60);
              }}
            >
              {t(`${cp}.start`)}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              loading={stop.isPending}
              disabled={busy || status?.enabled !== true}
              onClick={() => {
                stop.mutate();
              }}
            >
              {t(`${cp}.stop`)}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              loading={clear.isPending}
              disabled={busy}
              onClick={() => {
                clear.mutate();
              }}
            >
              {t(`${cp}.clear`)}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground" role="status">
            {status?.enabled === true
              ? t(`${cp}.statusOn`, {
                  seconds: status.secondsRemaining,
                  count: status.sessionCount,
                })
              : t(`${cp}.statusOff`, { count: status?.sessionCount ?? 0 })}
          </p>

          {/* Session list */}
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t(`${cp}.cols.time`)}</th>
                  <th className="px-3 py-2 font-medium">{t(`${cp}.cols.subject`)}</th>
                  <th className="px-3 py-2 font-medium">{t(`${cp}.cols.outcome`)}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {(sessionsQuery.data ?? []).length === 0 && (
                  <tr>
                    <td className="px-3 py-3 text-muted-foreground" colSpan={4}>
                      {t(`${cp}.empty`)}
                    </td>
                  </tr>
                )}
                {(sessionsQuery.data ?? []).map((s) => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                      {new Date(s.capturedAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{s.subject ?? '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline">{s.outcome}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          void view(s.id);
                        }}
                      >
                        {t(`${cp}.view`)}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {viewError && (
            <p role="alert" className="text-xs text-red-600">
              {t(`${cp}.viewError`)}
            </p>
          )}
        </div>
      )}

      <Dialog
        open={viewing !== null}
        onOpenChange={(next) => {
          if (!next) setViewing(null);
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t(`${cp}.modalTitle`)}</DialogTitle>
          </DialogHeader>
          <pre className="max-h-[70vh] overflow-auto rounded-md bg-muted p-4 text-xs">
            {viewing === null ? '' : JSON.stringify(viewing, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </section>
  );
}
